import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useGetMetadata } from '../../hooks/useGetMetadata';
import { Heading, Button, CircularProgress } from '@chakra-ui/react';
import { ModalDialog } from '../../components/ModalDialog';
import { getInterviewResultAnswerHistoryList } from '../../api/index';
import { GetAnswerHistoryList } from '../../types/answerHistoryApiTypes';
import { InterviewAnswerHistoryListTable } from '../../components/InterviewAnswerListTable';
import { dateConvert } from '../../utils/dateConvert';
import { NoData } from '../../components/NoData';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};
export type DataType = {
  interviewAnswerHistoryId: string;
  createdAt: string;
  createdByName: string;
};

export const InterviewResultHistoryList: React.VFC<Props> = ({
  isOpen,
  onClose,
}) => {
  const { id } = useParams<{ id: string }>();
  const { user, getAccessTokenSilently } = useAuth0();
  const { auth0UserData } = useGetMetadata();
  const { tenantId } = auth0UserData;
  const userId = user?.sub ?? '';
  const userName = encodeURI(user?.name ?? '');
  const [loading, setLoading] = useState(false);
  const [answerHistoryList, setAnswerHistoryList] = useState<DataType[]>([]);
  const [status, setStatus] = useState(200);

  const handleOnClose = () => {
    onClose();
  };

  useEffect(() => {
    const getData = async (): Promise<void> => {
      try {
        setLoading(true);
        const accessToken = await getAccessTokenSilently({
          audience: 'https://www.webinterview.mic.jp/',
          scope: 'read:interview-results',
        });
        const result = await getInterviewResultAnswerHistoryList(
          tenantId,
          id,
          userId,
          userName,
          accessToken,
        );
        if (result.ok) {
          const data: GetAnswerHistoryList = await result.json();
          setAnswerHistoryList(
            data.map<DataType>((x) => ({
              interviewAnswerHistoryId: x.id,
              createdAt: dateConvert(x.created_at, 'seconds'),
              createdByName: x.created_by_name,
            })),
          );
        } else {
          setStatus(result.status);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [getAccessTokenSilently, tenantId, id, userId, userName]);

  return (
    <>
      <ModalDialog
        header={
          <Heading
            size="lg"
            color="gray.600"
            textAlign="left"
            pb="4"
            borderBottomWidth="1px"
          >
            質問と回答 履歴一覧
          </Heading>
        }
        body={
          loading ? (
            <CircularProgress isIndeterminate color="blue.300" />
          ) : status === 404 ? (
            <NoData />
          ) : (
            <>
              <InterviewAnswerHistoryListTable
                loading={loading}
                answerHistoryList={answerHistoryList}
              />
            </>
          )
        }
        footer={
          <Button w="30" onClick={handleOnClose}>
            戻る
          </Button>
        }
        isOpen={isOpen}
        onClose={handleOnClose}
        size="2xl"
      />
    </>
  );
};
